import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, Eye, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  status: string;
  publish_date: string | null;
  created_at: string;
  blog_categories: {
    name: string;
  } | null;
}

export default function AdminPosts() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletePostId, setDeletePostId] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select(`
        id,
        title,
        slug,
        status,
        publish_date,
        created_at,
        blog_categories (name)
      `)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setPosts(data);
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!deletePostId) return;

    const { error } = await supabase
      .from("blog_posts")
      .delete()
      .eq("id", deletePostId);

    if (error) {
      toast.error("Failed to delete post");
      return;
    }

    toast.success("Post deleted successfully");
    setPosts(posts.filter((p) => p.id !== deletePostId));
    setDeletePostId(null);
  };

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div>
          <div className="flex items-center justify-between mt-4 mb-8">
            <h1 className="text-3xl font-bold">Blog Posts</h1>
            <Link to="/admin/posts/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Post
              </Button>
            </Link>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : posts.length === 0 ? (
            <Card>
              <CardContent className="py-20 text-center">
                <p className="text-muted-foreground mb-4">No blog posts yet</p>
                <Link to="/admin/posts/new">
                  <Button>Create Your First Post</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <Card key={post.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle className="text-xl">{post.title}</CardTitle>
                          <Badge variant={post.status === "published" ? "default" : "secondary"}>
                            {post.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {post.blog_categories && (
                            <span>{post.blog_categories.name}</span>
                          )}
                          <span>
                            {post.status === "published" && post.publish_date
                              ? `Published ${format(new Date(post.publish_date), "MMM d, yyyy")}`
                              : `Created ${format(new Date(post.created_at), "MMM d, yyyy")}`}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {post.status === "published" && (
                          <Link to={`/blog/${post.slug}`} target="_blank">
                            <Button variant="ghost" size="icon" aria-label="View published post">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        <Link to={`/admin/posts/edit/${post.id}`}>
                          <Button variant="ghost" size="icon" aria-label="Edit post">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletePostId(post.id)}
                          aria-label="Delete post"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>

        <AlertDialog open={deletePostId !== null} onOpenChange={() => setDeletePostId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the blog post.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Floating Create Post Button */}
        <Link to="/admin/posts/new">
          <Button
            size="lg"
            className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-shadow z-50 md:h-auto md:w-auto md:rounded-md md:px-6"
          >
            <Plus className="h-6 w-6 md:mr-2" />
            <span className="hidden md:inline">New Post</span>
          </Button>
        </Link>
      </AdminLayout>
    </ProtectedRoute>
  );
}
